import React, { useEffect, useState, useMemo, useCallback } from "react";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";

import CircularLoader from "components/common/CircularLoader/CircularLoader";
import CheckBox from "components/common/checkBox/checkbox";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { TOAST_TYPE, SHIFT_STATUS } from "Constants/constant";
import RosteringAPI from "store/services/rostering.services";
import { workerAndParticipantShiftsBulkDeleteLocally } from "store/actions/rostering.actions";
import {
  capitalizeFirstLetter,
  formatDateIntoDMY,
  formatDateIntoDMMMY,
  formatDateIntoDMYToDisplay,
} from "Utils/utils";
import { showToast } from "store/actions/general.actions";
import { TOAST_IDS } from "store/constants";
import moment from "moment";
import SelectOption from "components/common/Select/Select";
import useCareLabels from "hooks/useCareLabels";
import PublishShiftPersonCell from "../PublishShiftModal/PublishShiftPersonCell";
import PublishShiftAdvanceBadges from "../PublishShiftModal/PublishShiftAdvanceBadges";
import {
  getPublishShiftDisplayMeta,
  getPublishShiftParticipants,
  getPublishShiftWorkers,
} from "../PublishShiftModal/publishShiftModalDisplay";
import { isAdvanceMultiAssignmentShift } from "../../Calendar/advanceShiftCalendarUtils";

const DeleteShiftsModal = (props) => {
  const {
    isShow,
    handleClose,
    calendarView,
    isUserGracePeriodOver,
    selectedDateTypes,
    handleGetShiftStats,
    startDate,
    endDate,
    selectedOps,
    participantNameLabel: participantNameLabelProp,
    participantLabelPlural: participantLabelPluralProp,
    refreshCalendar,
  } = props;
  const {
    participantNameLabel: participantNameLabelHook,
    participantLabelPlural: participantLabelPluralHook,
  } = useCareLabels();
  const participantNameLabel = participantNameLabelProp ?? participantNameLabelHook;
  const participantLabelPlural = participantLabelPluralProp ?? participantLabelPluralHook;

  const dispatch = useDispatch();

  const [checkboxes, setCheckboxes] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shifts, setModalShifts] = useState([]);
  const [filteredShifts, setFilteredShifts] = useState([]);
  const [selectedWorkerFilter, setSelectedWorkerFilter] = useState(null);
  const [selectedParticipantFilter, setSelectedParticipantFilter] = useState(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);

  const fetchModalShifts = async () => {
    if (!calendarView || !startDate || !endDate) return;

    setIsLoading(true);

    const baseParams = {
      per_page: 500,
      from_date: formatDateIntoDMY(startDate),
      to_date: formatDateIntoDMY(endDate),
      sort: "asc",
    };

    if (
      calendarView === "worker-view" &&
      selectedOps !== null &&
      selectedOps?.length > 0
    ) {
      baseParams.workers = selectedOps.map((x) => x.value);
    } else if (
      calendarView === "participant-view" &&
      selectedOps?.length > 0 &&
      selectedOps !== null
    ) {
      baseParams.participants = selectedOps.map((x) => x.value);
    }

    try {
      // API pages at 500; keep requesting until every page is loaded.
      let page = 1;
      let lastPage = 1;
      const allShifts = [];

      do {
        const response = await RosteringAPI.getDeletableShiftsList({
          ...baseParams,
          page,
        });
        const payload =
          response?.data?.deletable_shifts || response?.deletable_shifts || {};
        const pageShifts = Array.isArray(payload?.shifts)
          ? payload.shifts
          : Array.isArray(payload)
          ? payload
          : [];
        allShifts.push(...pageShifts);

        const meta = payload?.meta || {};
        lastPage = Math.max(1, Number(meta.last_page) || 1);
        page += 1;
      } while (page <= lastPage);

      setModalShifts(allShifts);
      setFilteredShifts(allShifts);
    } catch (error) {
      toast.error(error?.message || "Error fetching shifts");
      setModalShifts([]);
      setFilteredShifts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isShow) {
      fetchModalShifts();
      setCheckboxes([]);
      setSelectedWorkerFilter(null);
      setSelectedParticipantFilter(null);
      setSelectedStatusFilter(null);
    }
  }, [startDate, endDate, calendarView, isShow]);

  // Get unique values for filter options (include V5 advance workers[] / participants[])
  const workerOptions = useMemo(() => {
    const uniqueWorkers = new Set();
    shifts.forEach((shift) => {
      getPublishShiftWorkers(shift).forEach((worker) => {
        const name =
          worker?.name ||
          `${worker?.first_name || ""} ${worker?.last_name || ""}`.trim();
        if (name && name !== "undefined undefined") {
          uniqueWorkers.add(name);
        }
      });
    });
    return Array.from(uniqueWorkers)
      .sort()
      .map((name) => ({ label: name, value: name }));
  }, [shifts]);

  const participantOptions = useMemo(() => {
    const uniqueParticipants = new Set();
    shifts.forEach((shift) => {
      getPublishShiftParticipants(shift).forEach((participant) => {
        const name =
          participant?.name ||
          `${participant?.first_name || ""} ${
            participant?.last_name || ""
          }`.trim();
        if (name) uniqueParticipants.add(name);
      });
    });
    return Array.from(uniqueParticipants)
      .sort()
      .map((name) => ({ label: name, value: name }));
  }, [shifts]);

  // Helper function to check if a shift is open (no worker assigned)
  const isOpenShift = useCallback((shift) => {
    if (isAdvanceMultiAssignmentShift(shift)) {
      return getPublishShiftWorkers(shift).length === 0;
    }
    return (
      !shift?.worker ||
      shift?.worker === null ||
      shift?.worker?.name === "undefined undefined" ||
      shift?.worker?.name === undefined
    );
  }, []);

  // Helper function to get status display label (matches table display logic)
  const getStatusDisplayLabel = useCallback((shift) => {
    if (shift.status === SHIFT_STATUS.NA) {
      return "In-Progress";
    } else if (
      shift.status === SHIFT_STATUS.ACCEPTED &&
      shift.progress === "submitted"
    ) {
      return "Completed";
    } else if (
      shift.status === SHIFT_STATUS.PUBLISHED &&
      shift?.shift_accept === false
    ) {
      return "Accepted";
    } else {
      return capitalizeFirstLetter(shift.status || "");
    }
  }, []);

  const statusOptions = useMemo(() => {
    // Use Set to store unique display labels (as they appear in the table)
    const uniqueLabels = new Set();
    let hasOpenShifts = false;

    shifts.forEach((shift) => {
      if (shift?.status) {
        const displayLabel = getStatusDisplayLabel(shift);
        uniqueLabels.add(displayLabel);
      }
      // Check if there are any open shifts
      if (isOpenShift(shift)) {
        hasOpenShifts = true;
      }
    });

    // Convert to array and sort
    const statusArray = Array.from(uniqueLabels)
      .sort()
      .map((label) => ({
        label: label,
        value: label, // Use display label as value for filtering
      }));

    // Add "Open" status option if there are open shifts
    if (hasOpenShifts) {
      statusArray.push({ label: "Open Shift", value: "open" });
    }

    return statusArray;
  }, [shifts, isOpenShift, getStatusDisplayLabel]);

  // Filter shifts based on selected filters
  useEffect(() => {
    // Create a new array reference to ensure React detects the change
    let filtered = shifts.length > 0 ? [...shifts] : [];

    if (selectedWorkerFilter) {
      filtered = filtered.filter((shift) =>
        getPublishShiftWorkers(shift).some((worker) => {
          const name =
            worker?.name ||
            `${worker?.first_name || ""} ${worker?.last_name || ""}`.trim();
          return name === selectedWorkerFilter.value;
        })
      );
    }

    if (selectedParticipantFilter) {
      filtered = filtered.filter((shift) =>
        getPublishShiftParticipants(shift).some((participant) => {
          const name =
            participant?.name ||
            `${participant?.first_name || ""} ${
              participant?.last_name || ""
            }`.trim();
          return name === selectedParticipantFilter.value;
        })
      );
    }

    if (selectedStatusFilter) {
      // Handle "Open" status filter separately (based on worker assignment)
      if (selectedStatusFilter.value === "open") {
        filtered = filtered.filter((shift) => isOpenShift(shift));
      } else {
        // Filter by display label (matches table display logic)
        filtered = filtered.filter((shift) => {
          const displayLabel = getStatusDisplayLabel(shift);
          return displayLabel === selectedStatusFilter.value;
        });
      }
    }

    // Always create a new array reference to ensure React detects the change
    setFilteredShifts([...filtered]);
    // Reset checkboxes when filters change
    setCheckboxes([]);
  }, [selectedWorkerFilter, selectedParticipantFilter, selectedStatusFilter, shifts, isOpenShift, getStatusDisplayLabel]);
  const handleDeleteShifts = async () => {
    if (isUserGracePeriodOver) {
      return;
    }
    if (checkboxes.length === 0) {
      dispatch(
        showToast({
          id: TOAST_IDS.PUBLISH_SHIFT_MODAL_BUTTON_ID,
          type: TOAST_TYPE.DEFAULT,
          title: "No shifts selected!",
        })
      );
      return;
    }

    try {
      setIsLoading(true);
      
      // Use optimized bulk delete endpoint for better performance
      const uuids = checkboxes
        .map((shift) => shift.uuid)
        .filter((uuid) => uuid != null && uuid !== "");
      
      if (uuids.length === 0) {
        dispatch(
          showToast({
            id: TOAST_IDS.PUBLISH_SHIFT_MODAL_BUTTON_ID,
            type: TOAST_TYPE.Error,
            title: "No valid shifts selected!",
          })
        );
        setIsLoading(false);
        return;
      }

      const response = await RosteringAPI.bulkDeleteWorkerAndParticipantShifts({
        uuids: uuids,
        recurring_impact: false, // For bulk delete, we delete only selected shifts
      });

      if (response) {
        const payload = response?.data;
        const toastMessage =
          (typeof payload === "object" && payload?.message) ||
          (typeof payload === "string" ? payload : null) ||
          `${uuids.length} shift(s) deleted successfully!`;

        toast.success(toastMessage);

        const shiftsToDelete = checkboxes
          .map((shift) => {
            const userId =
              calendarView === "worker-view"
                ? shift.worker_id
                : shift.pt_id ||
                  (shift.participants?.length > 0
                    ? shift.participants[0]?.id
                    : null);

            if (userId) {
              return {
                shiftUuid: shift.uuid,
                userId: userId,
              };
            }
            return null;
          })
          .filter(Boolean);

        if (shiftsToDelete.length > 0) {
          dispatch(
            workerAndParticipantShiftsBulkDeleteLocally({
              shifts: shiftsToDelete,
              calendarView:
                calendarView === "worker-view" ? "worker" : "participant",
            })
          );
        }

        const needsDelayedRefresh =
          payload &&
          typeof payload === "object" &&
          (payload.queued === true || payload.pivot_cleanup_queued === true);
        if (needsDelayedRefresh && typeof refreshCalendar === "function") {
          refreshCalendar({ isRefreshCall: true });
          setTimeout(() => {
            refreshCalendar({ isRefreshCall: true });
          }, 1200);
        }
      }

      // Update shift stats
      if (selectedOps?.length > 0) {
        handleGetShiftStats(startDate, endDate, selectedOps);
      } else {
        handleGetShiftStats(startDate, endDate);
      }

      setIsLoading(false);
      handleClose();
    } catch (err) {
      setIsLoading(false);
      console.log("err", err);
      toast.error(err?.message ?? "Something went wrong!");
    }
  };

  const getShiftKey = useCallback((shift) => {
    if (shift?.uuid != null && shift.uuid !== "") {
      return String(shift.uuid);
    }
    if (shift?.id != null && shift.id !== "") {
      return `id-${shift.id}`;
    }
    // Last-resort stable key when uuid/id are missing (no list index — must match across arrays)
    return [
      shift?.shift_date,
      shift?.start_time,
      shift?.end_time,
      shift?.worker_id,
      shift?.pt_id,
      shift?.worker?.name,
      shift?.status,
    ].join("|");
  }, []);

  const selectedShiftKeys = useMemo(() => {
    return new Set(checkboxes.map((shift) => getShiftKey(shift)));
  }, [checkboxes, getShiftKey]);

  const toggleCheckboxes = useCallback(
    (val) => {
      setCheckboxes(val ? [...filteredShifts] : []);
    },
    [filteredShifts]
  );

  // Header is checked only when every visible row is selected (by stable key)
  const allSelectedMemo = useMemo(() => {
    if (!filteredShifts?.length) return false;
    return filteredShifts.every((shift) =>
      selectedShiftKeys.has(getShiftKey(shift))
    );
  }, [filteredShifts, selectedShiftKeys, getShiftKey]);

  useEffect(() => {
    setAllSelected(allSelectedMemo);
  }, [allSelectedMemo]);

  const handleCheckboxChange = useCallback(
    (checked, shift) => {
      const key = getShiftKey(shift);
      setCheckboxes((prev) => {
        const withoutCurrent = prev.filter(
          (item) => getShiftKey(item) !== key
        );
        if (checked) {
          withoutCurrent.push({ ...shift });
        }
        return withoutCurrent;
      });
    },
    [getShiftKey]
  );

  const modalFooter = () => (
    <>
      <CustomButton
        variant="danger"
        clickHandler={() => {
          handleClose();
        }}
        label="Cancel"
      />
      <CustomButton
        variant="danger"
        clickHandler={handleDeleteShifts}
        showLoading={isLoading}
        isDisabled={isLoading || isUserGracePeriodOver}
        label="Delete"
      />
    </>
  );

  // Memoize the table rows to prevent unnecessary re-renders
  const tableRows = useMemo(() => {
    if (!filteredShifts?.length) return null;
    
    return filteredShifts.map((shift, index) => {
      const shiftKey = getShiftKey(shift);
      const displayMeta = getPublishShiftDisplayMeta(shift);
      return (
      <tr
        key={`${shiftKey}-${index}`}
        className={`border-t ${displayMeta.rowAccentClass || ""}`}
      >
        <td className="px-4 py-3 text-center">
          <CheckBox
            checked={selectedShiftKeys.has(shiftKey)}
            handleChange={(checked) =>
              handleCheckboxChange(checked, shift)
            }
          />
        </td>
        <td className="px-4 text-sm py-3">
          <div className="flex flex-col gap-1">
            <PublishShiftPersonCell
              people={displayMeta.workers}
              showAvatars={displayMeta.showWorkerAvatars}
              emptyLabel={displayMeta.workerEmptyLabel}
              openSlotCount={displayMeta.openSlotCount}
              type="worker"
            />
            {isAdvanceMultiAssignmentShift(shift) ? (
              <PublishShiftAdvanceBadges
                label={displayMeta.shiftTypeLabel}
                className={displayMeta.shiftTypeClassName}
              />
            ) : null}
          </div>
        </td>
        <td className="px-4 text-sm py-3">
          <PublishShiftPersonCell
            people={displayMeta.participants}
            showAvatars={displayMeta.showParticipantAvatars}
            emptyLabel={`No ${String(participantNameLabel || "participant")
              .replace(/ Name$/i, "")
              .toLowerCase()} assigned`}
            type="participant"
          />
        </td>
        <td className="px-4 py-3 text-sm text-center">
          <div className="text-gray-500">
            {formatDateIntoDMYToDisplay(shift.shift_date)}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-center">
          <div className="text-gray-500">
            {shift.shift_length && shift.shift_length.length > 4
              ? shift?.shift_length?.slice(0, 5)?.split(":").length >= 1 &&
                `${shift?.shift_length?.slice(0, 5)?.split(":")[0]}h ${
                  shift?.shift_length?.slice(0, 5)?.split(":")[1]
                }m`
              : shift.shift_length}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-center">
          <div className="text-gray-500">
            {moment(shift.start_time, "YYYY-MM-DD HH:mm:ss").format("hh:mm a")}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-center">
          <div className="text-gray-500">
            {moment(shift.end_time, "YYYY-MM-DD HH:mm:ss").format("hh:mm a")}
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-center">
          <div className="text-gray-500 capitalize">
            {shift.status === SHIFT_STATUS.NA
              ? "In-Progress"
              : shift.status === SHIFT_STATUS.ACCEPTED &&
                shift.progress === "submitted"
              ? "Completed"
              : shift.status === SHIFT_STATUS.PUBLISHED &&
                shift?.shift_accept === false
              ? "Accepted"
              : shift.status}
          </div>
        </td>
      </tr>
      );
    });
  }, [
    filteredShifts,
    selectedShiftKeys,
    handleCheckboxChange,
    getShiftKey,
    participantNameLabel,
  ]);

  const deleteShiftContent = () => {
    return (
      <div className="p-4">
        {/* Filter Section */}
        <div className="mb-4 pb-4 border-b">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Worker Name Filter */}
            <div className="flex flex-col">
              <label className="text-sm text-gray-700 font-medium mb-1.5">
                Worker Name
              </label>
              <SelectOption
                cssClass=""
                isSearch={true}
                isClearable={true}
                selectedOption={selectedWorkerFilter}
                handleChange={setSelectedWorkerFilter}
                data={workerOptions}
                placeHolder="All Workers"
              />
            </div>

            {/* Participant/Consumer Name Filter */}
            <div className="flex flex-col">
              <label className="text-sm text-gray-700 font-medium mb-1.5">
                {participantNameLabel}
              </label>
              <SelectOption
                cssClass=""
                isSearch={true}
                isClearable={true}
                selectedOption={selectedParticipantFilter}
                handleChange={setSelectedParticipantFilter}
                data={participantOptions}
                placeHolder={`All ${participantLabelPlural}`}
              />
            </div>

            {/* Status Filter */}
            <div className="flex flex-col">
              <label className="text-sm text-gray-700 font-medium mb-1.5">
                Status
              </label>
              <SelectOption
                cssClass=""
                isSearch={false}
                isClearable={true}
                selectedOption={selectedStatusFilter}
                handleChange={setSelectedStatusFilter}
                data={statusOptions}
                placeHolder="All Statuses"
              />
            </div>
          </div>
        </div>

        {/* Total Shifts Count */}
        <div className="py-2 mb-2">
          <h4>
            <b>Total Shifts:</b> {filteredShifts?.length || 0}
            {shifts?.length !== filteredShifts?.length && (
              <span className="text-gray-500 text-sm font-normal ml-2">
                (of {shifts?.length || 0} total)
              </span>
            )}
          </h4>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <div className="max-h-[350px] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="bg-blue-50 text-left sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-4 w-10 text-center">
                    <CheckBox
                      checked={allSelected}
                      handleChange={(e) => toggleCheckboxes(e)}
                    />
                  </th>
                  <th className="px-4 py-3 text-sm">Worker Name</th>
                  <th className="px-4 py-3 text-sm">{participantNameLabel}</th>
                  <th className="px-4 py-3 text-center text-sm">Shift Date</th>
                  <th className="px-4 py-3 text-center text-sm">
                    Shift Length
                  </th>
                  <th className="px-4 py-3 text-center text-sm">Start Time</th>
                  <th className="px-4 py-3 text-center text-sm">End Time</th>
                  <th className="px-4 py-3 text-center text-sm">Status</th>
                </tr>
              </thead>
              <tbody key={`tbody-${filteredShifts?.length || 0}-${selectedWorkerFilter?.value || ''}-${selectedParticipantFilter?.value || ''}-${selectedStatusFilter?.value || ''}`}>
                {filteredShifts?.length > 0 ? (
                  tableRows
                ) : (
                  <tr>
                    <td colSpan={8} className="py-6 text-center text-red-600">
                      {shifts?.length > 0
                        ? "No shifts match the selected filters!"
                        : "No shifts available to delete!"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">This action will be recorded in the audit log.</p>
      </div>
    );
  };

  return (
    <div>
      {isShow && (
        <ModalSkeleton
          title={`Delete Shifts From ${formatDateIntoDMMMY(
            selectedDateTypes?.startDate
          )} To ${formatDateIntoDMMMY(selectedDateTypes?.endDate)}`}
          isOpen={isShow}
          cssClass="w-[60%]"
          bodyPadding="px-0"
          closeModal={() => {
            handleClose();
          }}
          modalFooter={modalFooter()}
        >
          {isLoading ? (
            <div
              className={`flex flex-col w-full min-h-screen bg-gray-50 items-center justify-center`}
            >
              <CircularLoader
                classes="flex justify-center items-center"
                classloader="loader-l"
              />
            </div>
          ) : (
            deleteShiftContent()
          )}
        </ModalSkeleton>
      )}
    </div>
  );
};

export default DeleteShiftsModal;

